import { useState, useEffect, useMemo } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../../services/api'
import {
  ArrowLeft, ChevronRight, ExternalLink, Play, Download, FileText,
  Link as LinkIcon, Dumbbell, BookOpen, Youtube, GraduationCap,
  PenLine, AlertCircle, Sparkles, ArrowRight, Search,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import { extractYouTubeId, youTubeEmbedUrl, youTubeThumbnail, youTubeWatchUrl } from '../../utils/youtube'

// ─── Resource type metadata ─────────────────────────────────────────────────
// Single source of truth for type-aware presentation. The `kind` drives the
// primary action label and the detail-page section language; `icon` provides
// restrained visual identity without fake thumbnails.
//
// Student-facing labels differ slightly from the backend type names where it
// helps clarity ("Files" rather than "Downloadable File" — download is an
// action, not a category). The authoritative backend resource_type is always
// used for filtering and grouping; only the display label changes.
const TYPE_META = {
  youtube:   { label: 'Videos',         navLabel: 'Videos',         icon: Youtube,    accent: 'text-red-500 dark:text-red-400',     action: 'Watch',     whileVerb: 'while watching',  openLabel: 'Open on YouTube' },
  article:   { label: 'Article',        navLabel: 'Articles',       icon: LinkIcon,   accent: 'text-primary-600 dark:text-primary-400', action: 'Read',  whileVerb: 'while reading',   openLabel: 'Open Article' },
  pdf:       { label: 'PDF / Document',  navLabel: 'Documents',      icon: FileText,   accent: 'text-blue-500 dark:text-blue-400',    action: 'Open',      whileVerb: 'while reading',   openLabel: 'Open Document' },
  file:      { label: 'File',           navLabel: 'Files',          icon: Download,   accent: 'text-amber-500 dark:text-amber-400', action: 'Download',  whileVerb: 'while using',     openLabel: 'Download File' },
  practice:  { label: 'Practice Asset',  navLabel: 'Practice Assets', icon: Dumbbell,   accent: 'text-emerald-500 dark:text-emerald-400', action: 'Use Asset', whileVerb: 'while using',  openLabel: 'Download Asset' },
  reference: { label: 'Reference',       navLabel: 'References',     icon: BookOpen,   accent: 'text-gray-500 dark:text-gray-400',   action: 'Open',      whileVerb: 'while reading',   openLabel: 'Open Resource' },
}

// Stable navigation order
const TYPE_ORDER = ['youtube', 'article', 'pdf', 'practice', 'file', 'reference']

// The curriculum view shows every type a lesson has, so the type row starts
// with an "All" entry; the six type tabs still filter the same hierarchy.
const ALL_TYPES = 'all'
const NAV_TYPES = [ALL_TYPES, ...TYPE_ORDER]
const ALL_TYPES_META = { label: 'All Resources', navLabel: 'All', icon: BookOpen, accent: 'text-gray-500 dark:text-gray-400' }

function typeMeta(type) {
  return TYPE_META[type] || TYPE_META.reference
}

// ─── Split a block of text into scannable items ─────────────────────────────
function splitLearningItems(text) {
  if (!text) return []
  const raw = String(text).trim()
  if (raw.includes('\n')) {
    const items = raw.split(/\n+/).map((s) => s.trim()).filter(Boolean)
    if (items.length > 1) return items
  }
  const numbered = raw.match(/(?:^|\s)(?:\d+[.)]|•|-)\s+(.+)/g)
  if (numbered && numbered.length > 1) {
    return numbered.map((s) => s.replace(/(?:^|\s)(?:\d+[.)]|•|-)\s+/, '').trim())
  }
  return []
}

// ─── Responsive 16:9 YouTube player with graceful fallback ───────────────────
function YouTubePlayer({ videoId, title }) {
  const [embedFailed, setEmbedFailed] = useState(false)
  const [thumbFailed, setThumbFailed] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setEmbedFailed(false)
    setThumbFailed(false)
    setLoaded(false)
  }, [videoId])

  if (!videoId || embedFailed || thumbFailed) {
    return <FallbackBlock videoId={videoId} />
  }

  return (
    <div className="w-full">
      <div
        className="relative w-full overflow-hidden bg-gray-900 dark:bg-black"
        style={{ aspectRatio: '16 / 9' }}
      >
        {!loaded && (
          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-800 animate-pulse" aria-hidden="true" />
        )}
        <iframe
          className="absolute inset-0 w-full h-full transition-opacity duration-300"
          style={{ opacity: loaded ? 1 : 0 }}
          src={youTubeEmbedUrl(videoId)}
          title={title || 'YouTube video player'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          onLoad={() => setLoaded(true)}
          onError={() => setEmbedFailed(true)}
        />
      </div>
      <img src={youTubeThumbnail(videoId)} alt="" className="hidden" onError={() => setThumbFailed(true)} />
    </div>
  )
}

function FallbackBlock({ videoId }) {
  return (
    <div
      className="w-full flex flex-col items-center justify-center text-center px-6 py-12 bg-gray-50 dark:bg-gray-900"
      style={{ aspectRatio: '16 / 9' }}
    >
      <AlertCircle className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-3" />
      <p className="text-sm font-medium text-gray-700 dark:text-gray-200" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        This video can't be played inside SMA.
      </p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 mb-4 max-w-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        Watch this resource on YouTube instead.
      </p>
      {videoId && (
        <a
          href={youTubeWatchUrl(videoId)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-4 py-2 min-h-[44px] text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          <ExternalLink className="w-4 h-4" /> Open on YouTube
        </a>
      )}
    </div>
  )
}

// ─── Section label — editorial hierarchy without cards ─────────────────────
function SectionLabel({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      {Icon && <Icon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />}
      <span className="text-xs uppercase tracking-[0.18em] font-medium text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
        {children}
      </span>
    </div>
  )
}

// ─── Resource row — type-aware editorial presentation ────────────────────────
// YouTube resources use a 16:9 thumbnail (genuinely a video). All other types
// use a compact type-indicator (icon + label) — no fake video-shaped box.
function ResourceRow({ resource, onOpen, showContext = true }) {
  const type = resource.resource_type || resource.type
  const meta = typeMeta(type)
  const Icon = meta.icon
  const yId = resource.youtube_id || resource.youtubeId
  const isVideo = type === 'youtube' && yId

  // Curriculum context: COURSE CODE · Module N · Lesson Title. Suppressed when
  // the row already sits under its course/module/lesson headings.
  const ctx = showContext ? resource.curriculumBreadcrumb : null

  return (
    <button
      onClick={() => onOpen(resource)}
      className="w-full text-left flex flex-col sm:flex-row items-start gap-4 sm:gap-6 py-5 group transition-colors hover:bg-gray-50/60 dark:hover:bg-white/[0.02] -mx-2 px-2"
    >
      {/* Visual slot: 16:9 thumbnail ONLY for video; type indicator otherwise */}
      <div className="w-full sm:w-48 md:w-56 shrink-0">
        {isVideo ? (
          <div className="relative w-full overflow-hidden bg-gray-100 dark:bg-gray-800" style={{ aspectRatio: '16 / 9' }}>
            <img
              src={youTubeThumbnail(yId)}
              alt=""
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextSibling.style.display = 'flex'
              }}
            />
            <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 dark:bg-gray-800">
              <Icon className="w-6 h-6 text-gray-400" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center bg-black/25 group-hover:bg-black/35 transition-colors">
              <div className="w-9 h-9 bg-white/95 text-gray-900 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                <Play className="w-4 h-4 fill-gray-900 ml-0.5" />
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2.5 py-1">
            <Icon className={`w-4 h-4 ${meta.accent} shrink-0`} />
            <span className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              {meta.label}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {ctx ? (
          <div className="text-[11px] font-mono font-medium text-amber-600 dark:text-amber-400/95 mb-1.5 tracking-wide truncate">
            {ctx}
          </div>
        ) : null}
        <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {resource.title}
        </h3>
        {resource.description && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1.5 line-clamp-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {resource.description}
          </p>
        )}
        <div className="flex items-center gap-3 mt-3 text-xs text-gray-400 dark:text-gray-500 flex-wrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {resource.creator && (
            <span className="font-medium text-gray-700 dark:text-gray-300">{resource.creator}</span>
          )}
          {resource.is_featured && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-primary-600 dark:text-primary-400">Primary</span>
          )}
          {/* Action hint — type-aware */}
          <span className="text-[10px] uppercase tracking-[0.14em] font-semibold text-gray-400 dark:text-gray-500">
            {meta.action}
          </span>
        </div>
      </div>

      <div className="hidden sm:flex self-center shrink-0 text-gray-300 dark:text-gray-600 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
        <ChevronRight className="w-5 h-5" />
      </div>
    </button>
  )
}

// ─── Curriculum hierarchy ───────────────────────────────────────────────────
// Course → Module/Week → Lesson → Resource type → Resource.
// Hierarchy is carried by typography, dividers and indentation rather than
// nested cards, in keeping with the existing page.

function moduleLabel(mod) {
  if (mod.weekNumber !== null && mod.weekNumber !== undefined) return `Week ${mod.weekNumber}`
  if (mod.moduleNumber !== null && mod.moduleNumber !== undefined) return `Module ${mod.moduleNumber}`
  return 'Curriculum'
}

function lessonOrdinal(lesson, fallbackIndex) {
  const n = lesson.lessonNumber !== null && lesson.lessonNumber !== undefined ? lesson.lessonNumber : fallbackIndex + 1
  return String(n).padStart(2, '0')
}

// A single resource-type block inside a lesson. Rendered only when the lesson
// actually has resources of this type — no empty type headings.
function LessonTypeGroup({ resourceType, resources, onOpen }) {
  if (!resources.length) return null
  const meta = typeMeta(resourceType)
  const Icon = meta.icon
  return (
    <div className="pt-4 first:pt-1">
      <div className="flex items-center gap-2 pb-1 border-b border-gray-100 dark:border-gray-800">
        <Icon className={`w-3.5 h-3.5 ${meta.accent}`} />
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-gray-400 dark:text-gray-500" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {meta.navLabel}
        </span>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {resources.map((r) => (
          <ResourceRow key={r.id} resource={r} onOpen={onOpen} showContext={false} />
        ))}
      </div>
    </div>
  )
}

function LessonSection({ lesson, index, onOpen }) {
  if (!lesson.groups.length) return null
  return (
    <section className="pt-6">
      <div className="flex items-baseline gap-3">
        <span className="text-xs font-mono font-semibold text-gray-400 dark:text-gray-500 shrink-0">
          {lessonOrdinal(lesson, index)}
        </span>
        <h4 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white leading-snug" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {lesson.lessonTitle}
        </h4>
      </div>
      <div className="sm:pl-9">
        {lesson.groups.map((g) => (
          <LessonTypeGroup key={g.resourceType} resourceType={g.resourceType} resources={g.resources} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}

function ModuleSection({ module: mod, onOpen }) {
  if (!mod.lessons.length) return null
  return (
    <section className="pt-8">
      <div className="pb-2 border-b border-gray-200 dark:border-gray-800">
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold text-primary-600 dark:text-primary-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {moduleLabel(mod)}
        </span>
        <h3 className="text-sm sm:text-base font-medium text-gray-600 dark:text-gray-300 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          {mod.moduleTitle}
        </h3>
      </div>
      <div className="space-y-1">
        {mod.lessons.map((lesson, i) => (
          <LessonSection key={lesson.lessonId} lesson={lesson} index={i} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}

function CourseSection({ course, onOpen }) {
  if (!course.modules.length) return null
  return (
    <section>
      {/* Course header — typography, not cards */}
      <div className="pt-8 pb-3 border-b-2 border-gray-900 dark:border-white">
        <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white leading-tight uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {course.courseTitle}
        </h2>
        {course.courseCode && (
          <span className="text-xs font-mono font-medium text-gray-500 dark:text-gray-400 mt-1 block">
            {course.courseCode}
          </span>
        )}
      </div>
      <div className="space-y-2">
        {course.modules.map((mod) => (
          <ModuleSection key={mod.moduleId ?? `nomodule-${course.courseId}`} module={mod} onOpen={onOpen} />
        ))}
      </div>
    </section>
  )
}

// ─── Resource detail (the curated learning experience) ───────────────────────
function ResourceDetail({ resourceId, allResources, onOpenOther }) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['digital-lab-resource', resourceId],
    queryFn: async () => {
      const { data } = await api.get(`/students/resources/${resourceId}`)
      return data.resource
    },
    enabled: !!resourceId,
  })

  useEffect(() => {
    if (data?.title) document.title = `${data.title} · SMA Digital Lab`
  }, [data])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [resourceId])

  if (isLoading) return <LoadingSpinner label="Loading resource" />

  if (isError || !data) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <AlertCircle className="w-10 h-10 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          We couldn't load this resource. It may have been moved or is no longer available.
        </p>
        <button
          onClick={() => navigate('/student/digital-lab/resources')}
          className="mt-4 inline-flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline min-h-[44px]"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to resources
        </button>
      </div>
    )
  }

  const resource = data
  const type = resource.resource_type || resource.type
  const meta = typeMeta(type)
  const Icon = meta.icon
  const yId = resource.youtube_id || resource.youtubeId || (type === 'youtube' ? extractYouTubeId(resource.url) : null)
  const isYouTube = type === 'youtube' && yId

  const others = (allResources || []).filter(
    (r) => r.id !== resource.id && (r.resource_type || r.type) !== 'practice'
  )

  const learningItems = splitLearningItems(resource.learning_purpose)

  return (
    <div className="w-full max-w-[1600px] mx-auto pb-12">
      {/* Top Header / Breadcrumb Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-gray-200/80 dark:border-gray-800">
        <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm flex-wrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
          <Link to="/student/digital-lab" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">Digital Lab</Link>
          <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
          <Link to="/student/digital-lab/resources" className="text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400">Resources</Link>
          <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600" />
          <span className="text-gray-900 dark:text-white font-medium truncate max-w-[60vw] sm:max-w-md">{resource.title}</span>
        </nav>
        <Link
          to="/student/digital-lab/resources"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors shrink-0"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to All Resources
        </Link>
      </div>

      {/* Title & Metadata Banner */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">
            <Icon className={`w-3.5 h-3.5 ${meta.accent}`} />
            {meta.label}
          </span>
          {resource.is_featured && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-primary-600 dark:text-primary-400">
              Primary Curriculum
            </span>
          )}
          {resource.creator && (
            <span className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              by <strong className="text-gray-800 dark:text-gray-200 font-medium">{resource.creator}</strong>
              {resource.category ? ` · ${resource.category}` : ''}
            </span>
          )}
        </div>
        <h1 className="text-2xl sm:text-3xl xl:text-4xl text-gray-900 dark:text-white leading-tight font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {resource.title}
        </h1>
        {resource.description && (
          <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mt-3 leading-relaxed max-w-4xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {resource.description}
          </p>
        )}
      </div>

      {/* Main Studio 2-Column Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-12 items-start">
        {/* Left Column: Player/Access and Core Learning Details */}
        <div className="lg:col-span-8 space-y-8 min-w-0">
          {/* YouTube: embedded player is the primary experience */}
          {isYouTube && (
            <section className="bg-gray-950 overflow-hidden border border-gray-200 dark:border-gray-800/80">
              <YouTubePlayer videoId={yId} title={resource.title} />
              <div className="p-4 bg-white dark:bg-gray-900 flex items-center justify-between flex-wrap gap-3 border-t border-gray-100 dark:border-gray-800">
                <p className="text-xs text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Watch inside SMA. YouTube is the content provider; SMA is your learning environment.
                </p>
                <a
                  href={youTubeWatchUrl(yId)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors min-h-[32px]"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open on YouTube
                </a>
              </div>
            </section>
          )}

          {/* Non-YouTube: type-aware primary action */}
          {!isYouTube && resource.url && (
            <section className="p-6 sm:p-8 border border-gray-200 dark:border-gray-800">
              <SectionLabel icon={Icon}>Access this {meta.label.toLowerCase()}</SectionLabel>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {type === 'file' || type === 'pdf'
                  ? 'Download or open this file in your browser or native viewer.'
                  : type === 'article'
                    ? 'Read this article on the original site. SMA is your learning environment.'
                    : type === 'practice'
                      ? 'Use this asset for hands-on practice. Download it to your workspace.'
                      : 'Open this resource in your browser or native viewer.'}
              </p>
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 min-h-[44px] text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-all"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                {type === 'file' || type === 'pdf' || type === 'practice' ? <Download className="w-4 h-4" /> : <ExternalLink className="w-4 h-4" />}
                {meta.openLabel}
              </a>
            </section>
          )}

          {/* UNDERSTAND — What you'll learn */}
          {resource.learning_purpose && (
            <section className="p-6 sm:p-8 border border-gray-200 dark:border-gray-800">
              <SectionLabel icon={GraduationCap}>What you'll learn</SectionLabel>
              {learningItems.length > 1 ? (
                <ol className="space-y-3.5 mt-4">
                  {learningItems.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-4">
                      <span className="text-xs font-mono font-bold text-primary-600 dark:text-primary-400 w-6 h-6 flex items-center justify-center shrink-0 mt-0.5 border border-primary-200 dark:border-primary-900/50" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {idx + 1}
                      </span>
                      <span className="text-sm sm:text-base text-gray-800 dark:text-gray-200 leading-relaxed font-normal" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed mt-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {resource.learning_purpose}
                </p>
              )}
            </section>
          )}

          {/* UNDERSTAND — type-aware instructor guidance */}
          {resource.instructor_note && (
            <section className="p-6 sm:p-8 border-l-2 border-amber-500/60 bg-amber-500/[0.03] dark:bg-amber-500/[0.06]">
              <SectionLabel icon={PenLine}>Instructor Guidance · {meta.whileVerb}</SectionLabel>
              <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 leading-relaxed mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {resource.instructor_note}
              </p>
            </section>
          )}

          {/* PRACTISE — type-agnostic hands-on practice */}
          {resource.practice_instruction && (
            <section className="p-6 sm:p-8 border-l-2 border-primary-500/60 bg-primary-500/[0.03] dark:bg-primary-500/[0.06]">
              <SectionLabel icon={Dumbbell}>Hands-on Practice</SectionLabel>
              <p className="text-sm sm:text-base text-gray-800 dark:text-gray-200 leading-relaxed mb-6 mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {resource.practice_instruction}
              </p>
              <Link
                to="/student/digital-lab/projects"
                className="inline-flex items-center gap-2 px-6 py-3.5 min-h-[44px] text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-all"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
              >
                <Dumbbell className="w-4 h-4" /> Continue to Workspace Projects <ArrowRight className="w-4 h-4" />
              </Link>
            </section>
          )}
        </div>

        {/* Right Column / Rail: Curriculum Context & Perspectives */}
        <aside className="lg:col-span-4 space-y-6">
          {/* Curriculum Context */}
          {resource.curriculumBreadcrumb ? (
            <div className="p-6 border border-gray-200 dark:border-gray-800">
              <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 font-semibold block mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Curriculum Context
              </span>
              <div className="text-xs font-mono font-medium text-amber-600 dark:text-amber-400/95 mb-4">
                {resource.curriculumBreadcrumb}
              </div>

              {resource.primaryCurriculum?.courseId && (
                <Link
                  to={`/student/courses/${resource.primaryCurriculum.courseId}?lesson=${resource.primaryCurriculum.lessonId}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                >
                  View Lesson in LMS <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              )}

              {resource.lessons?.length > 1 && (
                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 space-y-1.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  <span className="font-semibold block text-gray-700 dark:text-gray-300">Also linked to:</span>
                  {resource.lessons.filter(l => !l.isPrimary).map((l) => (
                    <div key={l.lessonId} className="truncate">
                      • {l.courseCode} ({l.lessonTitle})
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : resource.isAcademyWide ? (
            <div className="p-6 border border-gray-200 dark:border-gray-800">
              <span className="text-[10px] uppercase tracking-[0.18em] text-gray-400 dark:text-gray-500 font-semibold block mb-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Resource Scope
              </span>
              <div className="text-xs font-semibold text-primary-600 dark:text-primary-400 flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5" /> Academy Resource
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                This is an academy-wide learning asset available across all departments.
              </p>
            </div>
          ) : null}

          {/* More Perspectives / Related Resources */}
          {others.length > 0 && (
            <div className="p-6 border border-gray-200 dark:border-gray-800">
              <SectionLabel icon={Sparkles}>More perspectives</SectionLabel>
              <div className="space-y-3 mt-4 divide-y divide-gray-100 dark:divide-gray-800/80">
                {others.slice(0, 6).map((r, idx) => {
                  const om = typeMeta(r.resource_type || r.type)
                  const OIcon = om.icon
                  const oyId = r.youtube_id || r.youtubeId
                  const oIsVideo = (r.resource_type || r.type) === 'youtube' && oyId
                  return (
                    <button
                      key={r.id}
                      onClick={() => onOpenOther(r.id)}
                      className={`w-full text-left flex items-start gap-3.5 ${idx === 0 ? '' : 'pt-3.5'} group transition-colors`}
                    >
                      <div className="w-24 shrink-0">
                        {oIsVideo ? (
                          <div className="relative w-full overflow-hidden bg-gray-100 dark:bg-gray-800" style={{ aspectRatio: '16 / 9' }}>
                            <img src={youTubeThumbnail(oyId)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                              <Play className="w-3.5 h-3.5 text-white fill-white" />
                            </div>
                          </div>
                        ) : (
                          <div className="w-full flex items-center justify-center py-3 bg-gray-50 dark:bg-gray-900/60 border border-gray-100 dark:border-gray-800">
                            <OIcon className={`w-4 h-4 ${om.accent}`} />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {r.title}
                        </h4>
                        {r.creator && (
                          <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                            {r.creator}
                          </p>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  )
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function DigitalLabResources() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [activeType, setActiveType] = useState(ALL_TYPES) // type filter over the curriculum view
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (!id) document.title = 'Learning Resources · SMA Digital Lab'
  }, [id])

  // Fetch ALL accessible resources (no course filter — grouping is client-side)
  const { data, isLoading } = useQuery({
    queryKey: ['digital-lab-resources', 'my-courses'],
    queryFn: async () => {
      const res = await api.get('/students/resources', { params: { scope: 'my-courses' } })
      return res.data
    },
  })

  const resources = data?.resources || []
  // Curriculum hierarchy comes from the API, derived from the admin-managed
  // resource → lesson mappings. The page never groups resources on its own.
  const curriculum = data?.curriculum || []

  const resourcesById = useMemo(() => {
    const map = new Map()
    for (const r of resources) map.set(r.id, r)
    return map
  }, [resources])

  const matchesSearch = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return () => true
    return (r) =>
      r.title?.toLowerCase().includes(q) ||
      r.description?.toLowerCase().includes(q) ||
      r.creator?.toLowerCase().includes(q) ||
      r.curriculumBreadcrumb?.toLowerCase().includes(q) ||
      r.category?.toLowerCase().includes(q)
  }, [search])

  // Project the API hierarchy into render-ready nodes, applying the active
  // type filter and the search query. Curriculum order is preserved exactly as
  // the API returned it; lessons, modules and courses left with no matching
  // resources are dropped rather than rendered empty.
  const visibleCourses = useMemo(() => {
    const courses = []
    for (const course of curriculum) {
      const modules = []
      for (const mod of course.modules || []) {
        const lessons = []
        for (const lesson of mod.lessons || []) {
          const groups = []
          for (const group of lesson.resourceGroups || []) {
            if (activeType !== ALL_TYPES && group.resourceType !== activeType) continue
            const groupResources = (group.resourceIds || [])
              .map((rid) => resourcesById.get(rid))
              .filter((r) => r && matchesSearch(r))
            if (groupResources.length > 0) {
              groups.push({ resourceType: group.resourceType, resources: groupResources })
            }
          }
          if (groups.length > 0) lessons.push({ ...lesson, groups })
        }
        if (lessons.length > 0) modules.push({ ...mod, lessons })
      }
      if (modules.length > 0) courses.push({ ...course, modules })
    }
    return courses
  }, [curriculum, resourcesById, activeType, matchesSearch])

  const visibleCount = useMemo(
    () =>
      visibleCourses.reduce(
        (n, c) =>
          n +
          c.modules.reduce(
            (m, mod) => m + mod.lessons.reduce((l, lesson) => l + lesson.groups.reduce((g, grp) => g + grp.resources.length, 0), 0),
            0
          ),
        0
      ),
    [visibleCourses]
  )

  if (id) {
    return (
      <ResourceDetail
        resourceId={id}
        allResources={resources}
        onOpenOther={(otherId) => navigate(`/student/digital-lab/resources/${otherId}`)}
      />
    )
  }

  const hasAnyResources = resources.length > 0
  const hasTypeResults = visibleCount > 0

  return (
    <div className="w-full max-w-[1280px] mx-auto space-y-6">
      {/* Header */}
      <div className="page-header">
        <div>
          <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium mb-2 block" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Student · Digital Lab
          </span>
          <h1 className="text-2xl sm:text-3xl xl:text-4xl text-gray-900 dark:text-white font-extrabold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Learning Resources
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-2xl" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Curated SMA learning materials for understanding, practice and creation.
          </p>
        </div>
        <Link
          to="/student/digital-lab"
          className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 min-h-[44px] transition-colors"
          style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back to Digital Lab
        </Link>
      </div>

      {/* Resource-type navigation — all six permanent tabs, text-based with active underline */}
      <div className="flex items-center gap-6 overflow-x-auto py-1 border-b border-gray-100 dark:border-gray-800 no-scrollbar">
        {NAV_TYPES.map((t) => {
          const m = t === ALL_TYPES ? ALL_TYPES_META : typeMeta(t)
          const Icon = m.icon
          const isActive = activeType === t
          return (
            <button
              key={t}
              onClick={() => { setActiveType(t); setSearch('') }}
              className={`text-sm whitespace-nowrap shrink-0 pb-2 pt-1 transition-colors border-b-2 flex items-center gap-1.5 ${
                isActive
                  ? 'border-primary-500 text-gray-900 dark:text-white font-semibold'
                  : 'border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200 font-medium'
              }`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              <Icon className={`w-3.5 h-3.5 ${isActive ? m.accent : 'text-gray-400 dark:text-gray-500'}`} />
              {m.navLabel}
            </button>
          )
        })}
      </div>

      {/* Search */}
      {hasAnyResources && (
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={activeType === ALL_TYPES ? 'Search resources...' : `Search ${typeMeta(activeType).navLabel.toLowerCase()}...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-xs border-0 border-b border-gray-200 dark:border-gray-800 bg-transparent text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-primary-500 transition-colors"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          />
        </div>
      )}

      {/* Empty state: no enrolled courses / no resources at all */}
      {!isLoading && !hasAnyResources && (
        <div className="py-16 text-center border-t border-b border-gray-100 dark:border-gray-800">
          <GraduationCap className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
            No Learning Resources Yet
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Digital Lab resources are unlocked according to your active course enrollments and tuition progress.
          </p>
        </div>
      )}

      {/* Loading state */}
      {isLoading && <LoadingSpinner label="Loading resources" />}

      {/* Content: curriculum hierarchy (Course → Week/Module → Lesson → Type) */}
      {!isLoading && hasTypeResults && (
        <div className="space-y-4">
          {visibleCourses.map((course) => (
            <CourseSection
              key={course.courseId}
              course={course}
              onOpen={(r) => navigate(`/student/digital-lab/resources/${r.id}`)}
            />
          ))}
        </div>
      )}

      {/* Empty state: selected type has no results (after search) */}
      {!isLoading && hasAnyResources && !hasTypeResults && (
        <div className="py-16 text-center border-t border-b border-gray-100 dark:border-gray-800">
          {search.trim() ? (
            <>
              <Search className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                No Resources Found
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Try a different search term or choose another resource type.
              </p>
            </>
          ) : (
            <>
              {(() => {
                const m = activeType === ALL_TYPES ? ALL_TYPES_META : typeMeta(activeType)
                const Icon = m.icon
                return <Icon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              })()}
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {activeType === ALL_TYPES ? 'No Resources Yet' : `No ${typeMeta(activeType).navLabel} Yet`}
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md mx-auto" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {activeType === ALL_TYPES
                  ? 'There are no learning resources mapped to your current lessons yet.'
                  : `There are no ${typeMeta(activeType).navLabel.toLowerCase()} available for your current courses.`}
              </p>
            </>
          )}
        </div>
      )}
    </div>
  )
}
